import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { TableTooltip } from '../../../../components/general/table';
import { asyncGetLegalProceedings } from '../../../../../services/legalProceeding.service';
import {
    LegalProceeding,
    LegalProceedingFilter,
} from '../../../../../shared/types/legalProceedings/legalProceedings';
import { ISelect } from '../../../../../shared/types/shared';
import { LegalProceedingsListActions } from './actions';
import dayjs from 'dayjs';
import DateTimeFormat from '../../../../components/dateTimeFormat';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';

export const useLegalProceedingsList = (filters?: LegalProceedingFilter) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<LegalProceeding[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<LegalProceedingFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<LegalProceeding>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <LegalProceedingsListActions id={row.id} />,
        });
    }

    columns.push(
        {
            field: 'id',
            headerName: 'Id',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'dateReceipt',
            headerName: 'Fecha recepción',
            minWidth: 120,
            flex: 0.4,
            valueGetter: (row) =>
                dayjs(row.row.processData?.dateReceipt)
                    .format('YYYY-MM-DD')
                    .toString() || null,
        },
        {
            field: 'expirationDate',
            headerName: 'Fecha vencimiento',
            minWidth: 120,
            flex: 0.4,
            valueGetter: (row) => {
                const date = row.row.processData?.expirationDate;
                return date
                    ? dayjs(date).format('YYYY-MM-DD').toString()
                    : null;
            },
        },
        {
            field: 'fileNumber',
            headerName: 'Número expediente',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.processData?.fileNumber || ''} />
            ),
        },
        {
            field: 'namePersonSubscribe',
            headerName: 'Nombre persona suscribe',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={row.row.processData?.namePersonSubscribe || ''}
                />
            ),
        },
        {
            field: 'nameClaimant',
            headerName: 'Nombre accionante',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip value={row.row.processData?.nameClaimant || ''} />
            ),
        },
        {
            field: 'pqrIdentified',
            headerName: 'PQR identificada',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={row.row.processData?.pqrIdentified || ''}
                />
            ),
        },
        {
            field: 'policy',
            headerName: 'Póliza',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.processData?.policy || ''} />
            ),
        },
        {
            field: 'plate',
            headerName: 'Placa',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.processData?.plate || ''} />
            ),
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.processData?.status as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <DateTimeFormat dateAt={row.row.created_at} />,
        },
        {
            field: 'createdBy',
            headerName: 'Creado por',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.createdBy?.username || ''} />
            ),
        },
        {
            field: 'updated_at',
            headerName: 'Fecha actualización',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <DateTimeFormat dateAt={row.row.updated_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetLegalProceedings(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
